import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import { AlertTriangle, ArrowDown, ArrowUp, IndianRupee, TrendingUp, Zap } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { h2, h3, h4, p, textStyle } from '../../styles/typography';
import { getAllSites } from '../SiteManagement/siteApi';
import Card from '../ui/card';
import './InvisibleCostTracker.css';
import { listUtilityLogs } from './invisibleCostApi';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const formatCurrency = (amount) =>
  amount != null
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount)
    : '—';

const formatPercentage = (value) => {
  if (value == null) return '—';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

const UtilityMatrixDashboard = () => {
  // Log screen view
  useScreenLogging('UtilityMatrixDashboard', {
    component: 'UtilityMatrixDashboard',
    screenType: 'utility-matrix',
  });

  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  // Set default billing period to current month
  const getCurrentPeriod = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  };
  const [billingPeriod, setBillingPeriod] = useState(getCurrentPeriod());
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch sites
  useEffect(() => {
    const loadSites = async () => {
      try {
        const sitesData = await getAllSites();
        // console.log('[UtilityMatrixDashboard] getAllSites API response:', sitesData);
        setSites(sitesData || []);
      } catch (err) {
        console.error('Error loading sites:', err);
      }
    };
    loadSites();
  }, []);

  // Transform utility logs data to dashboard format
  const transformLogsToDashboardData = useCallback((logs, previousPeriodLogs = [], sitesMap = {}) => {
    if (!logs || logs.length === 0) {
      return {
        summary: {
          totalSpend: 0,
          spendPerGuestHour: 0,
          highestConsumingAppliance: null,
          monthOverMonthChange: 0,
        },
        utilityMatrix: [],
        chartData: {
          labels: [],
          datasets: [{ label: 'Cost (₹)', data: [], backgroundColor: [] }],
        },
        insights: [],
      };
    }

    // Calculate usage for each log and enrich with site name
    const logsWithUsage = logs.map((log) => {
      const usage = (log.meter_reading_end || 0) - (log.meter_reading_start || 0);
      const siteName = log.site_name || (log.site_id && sitesMap[log.site_id]?.site_name) || log.site_id || 'Unknown Site';
      return { ...log, usage: Math.max(0, usage), site_name: siteName };
    });

    // Calculate total spend
    const totalSpend = logsWithUsage.reduce((sum, log) => sum + (log.invoice_amount || 0), 0);

    // Calculate total guest hours (if available, otherwise use a default calculation)
    // Note: guest_hours might not be in the API response, so we'll calculate a default or use 0
    const totalGuestHours = logsWithUsage.reduce(
      (sum, log) => sum + (log.guest_hours || 0),
      0
    );
    const spendPerGuestHour = totalGuestHours > 0 ? totalSpend / totalGuestHours : 0;

    // Find highest consuming appliance (if appliance data is available)
    const applianceMap = {};
    logsWithUsage.forEach((log) => {
      if (log.appliance_id || log.appliance_consumption_trend) {
        const key = log.appliance_id || 'unknown';
        if (!applianceMap[key]) {
          applianceMap[key] = {
            appliance_id: log.appliance_id,
            appliance_name: log.appliance_name || 'Unknown Appliance',
            total_usage: 0,
            total_cost: 0,
          };
        }
        applianceMap[key].total_usage += log.usage || 0;
        applianceMap[key].total_cost += log.invoice_amount || 0;
      }
    });
    const highestConsumingAppliance =
      Object.values(applianceMap).length > 0
        ? Object.values(applianceMap).sort((a, b) => b.total_cost - a.total_cost)[0]
        : null;

    // Calculate month-over-month change
    const previousSpend = previousPeriodLogs.reduce(
      (sum, log) => sum + (log.invoice_amount || 0),
      0
    );
    const monthOverMonthChange =
      previousSpend > 0 ? ((totalSpend - previousSpend) / previousSpend) * 100 : 0;

    // Group by utility type
    const utilityTypeMap = {};
    logsWithUsage.forEach((log) => {
      const utilityType = log.utility_type || 'Other';
      if (!utilityTypeMap[utilityType]) {
        utilityTypeMap[utilityType] = {
          utility_type: utilityType,
          total_cost: 0,
          total_usage: 0,
          cost_per_guest_hour: 0,
          logs: [],
        };
      }
      utilityTypeMap[utilityType].total_cost += log.invoice_amount || 0;
      utilityTypeMap[utilityType].total_usage += log.usage || 0;
      utilityTypeMap[utilityType].logs.push(log);
    });

    // Calculate cost per guest hour for each utility type
    Object.values(utilityTypeMap).forEach((ut) => {
      const totalGH = ut.logs.reduce((sum, log) => sum + (log.guest_hours || 0), 0);
      ut.cost_per_guest_hour = totalGH > 0 ? ut.total_cost / totalGH : 0;
    });

    // Generate insights from logs
    const insights = logsWithUsage
      .filter(
        (log) =>
          log.leakage_alert_status &&
          log.leakage_alert_status !== 'none' &&
          log.leakage_alert_status !== ''
      )
      .map((log) => ({
        type: 'leakage',
        utility_type: log.utility_type || 'Unknown',
        site_name: log.site_name || log.site_id || 'Unknown Site',
        message: `Potential ${log.leakage_alert_status} leakage detected in ${log.utility_type || 'utility'} at ${log.site_name || log.site_id || 'site'}`,
        severity: log.leakage_alert_status === 'confirmed' ? 'high' : 'medium',
      }));

    // Prepare chart data
    const utilityTypes = Object.keys(utilityTypeMap);
    const chartColors = [
      'rgba(59, 130, 246, 0.8)',
      'rgba(16, 185, 129, 0.8)',
      'rgba(245, 158, 11, 0.8)',
      'rgba(239, 68, 68, 0.8)',
      'rgba(139, 92, 246, 0.8)',
    ];

    return {
      summary: {
        totalSpend,
        spendPerGuestHour,
        highestConsumingAppliance,
        monthOverMonthChange,
      },
      utilityMatrix: Object.values(utilityTypeMap),
      chartData: {
        labels: utilityTypes,
        datasets: [
          {
            label: 'Cost (₹)',
            data: utilityTypes.map((type) => utilityTypeMap[type].total_cost),
            backgroundColor: utilityTypes.map(
              (_, idx) => chartColors[idx % chartColors.length]
            ),
          },
        ],
      },
      insights,
    };
  }, []);

  // Fetch dashboard data
  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch current period logs
      const currentResult = await listUtilityLogs({
        siteId: selectedSite || undefined,
        period: billingPeriod,
      });
      // console.log('[UtilityMatrixDashboard] listUtilityLogs (current period) API response:', currentResult);

      if (!currentResult.success) {
        throw new Error(currentResult.error || 'Failed to fetch utility logs');
      }

      const currentLogs = currentResult.logs || [];

      // Fetch previous period logs for month-over-month comparison
      const previousPeriod = (() => {
        const [year, month] = billingPeriod.split('-').map(Number);
        const prevDate = new Date(year, month - 2, 1); // month - 2 because month is 1-indexed
        return `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
      })();

      let previousLogs = [];
      try {
        const previousResult = await listUtilityLogs({
          siteId: selectedSite || undefined,
          period: previousPeriod,
        });
        // console.log('[UtilityMatrixDashboard] listUtilityLogs (previous period) API response:', previousResult);
        if (previousResult.success) {
          previousLogs = previousResult.logs || [];
        }
      } catch (prevErr) {
        // If previous period fetch fails, just use empty array (not critical)
        console.warn('Could not fetch previous period data:', prevErr);
      }

      // Create sites map for quick lookup
      const sitesMap = {};
      sites.forEach((site) => {
        if (site.site_id) {
          sitesMap[site.site_id] = site;
        }
      });

      // Transform logs to dashboard format
      const dashboardData = transformLogsToDashboardData(currentLogs, previousLogs, sitesMap);
      setDashboardData(dashboardData);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, billingPeriod, sites, transformLogsToDashboardData]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Generate billing period options (last 12 months)
  const billingPeriodOptions = useMemo(() => {
    const options = [];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
      options.push({ value: period, label });
    }
    return options;
  }, []);

  // Chart configuration
  const chartData = useMemo(() => {
    if (!dashboardData?.chartData) return null;

    return {
      labels: dashboardData.chartData.labels,
      datasets: [
        {
          label: 'Cost (₹)',
          data: dashboardData.chartData.datasets[0].data,
          backgroundColor: [
            'rgba(59, 130, 246, 0.8)',
            'rgba(16, 185, 129, 0.8)',
            'rgba(245, 158, 11, 0.8)',
            'rgba(239, 68, 68, 0.8)',
            'rgba(139, 92, 246, 0.8)',
          ],
          borderColor: [
            'rgba(59, 130, 246, 1)',
            'rgba(16, 185, 129, 1)',
            'rgba(245, 158, 11, 1)',
            'rgba(239, 68, 68, 1)',
            'rgba(139, 92, 246, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };
  }, [dashboardData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `Cost: ${formatCurrency(context.parsed.y)}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => formatCurrency(value),
        },
      },
    },
  };

  // Get badge color for cost per guest hour
  const getCostPerGuestHourBadgeColor = (value) => {
    if (value < 20) return 'bg-green-100 text-green-800';
    if (value < 40) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (loading && !dashboardData) {
    return (
      <div className="utility-matrix-dashboard">
        <div className="utility-matrix-loading">
          <div className="invisible-cost-spinner" />
          <p className={textStyle(p, 'text-gray-600 mt-4')}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="utility-matrix-dashboard">
      {/* Header with site selector and billing period picker */}
      <header className="utility-matrix-header">
        <h1 className={textStyle(h2, 'text-gray-900')}>Utility Matrix Dashboard</h1>
        <div className="utility-matrix-filters">
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="utility-matrix-select"
          >
            <option value="">All Sites</option>
            {Array.isArray(sites) && [...sites]
                    .sort((a, b) => {
            const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
            const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((site) => (
                      <option key={site.site_id || site.id} value={site.site_id || site.id}>
                        {site.site_name || site.name || 'Unknown Site'}
                      </option>
                    ))}
          </select>
          <select
            value={billingPeriod}
            onChange={(e) => setBillingPeriod(e.target.value)}
            className="utility-matrix-select"
          >
            {billingPeriodOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </header>

      {error && (
        <div className="utility-matrix-error">
          <p className={textStyle(p, 'text-red-600')}>{error}</p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="utility-matrix-summary-cards">
        <Card variant="light" className="utility-matrix-summary-card">
          <IndianRupee className="utility-matrix-summary-icon text-teal-600" />
          <span className={textStyle(h3, 'utility-matrix-summary-value font-figtree font-bold')}>
            {formatCurrency(dashboardData?.summary?.totalSpend || 0)}
          </span>
          <span className={textStyle(p, 'utility-matrix-summary-label')}>Total Spend</span>
        </Card>

        <Card variant="light" className="utility-matrix-summary-card">
          <TrendingUp className="utility-matrix-summary-icon text-blue-600" />
          <span className={textStyle(h3, 'utility-matrix-summary-value font-figtree font-bold')}>
            {formatCurrency(dashboardData?.summary?.spendPerGuestHour || 0)}
          </span>
          <span className={textStyle(p, 'utility-matrix-summary-label')}>Spend per Guest Hour</span>
        </Card>

        <Card variant="light" className="utility-matrix-summary-card">
          <Zap className="utility-matrix-summary-icon text-amber-600" />
          <span className={textStyle(h4, 'utility-matrix-summary-value font-figtree font-bold')}>
            {dashboardData?.summary?.highestConsumingAppliance?.appliance_name || '—'}
          </span>
          <span className={textStyle(p, 'utility-matrix-summary-label')}>Highest Consuming Appliance</span>
        </Card>

        <Card variant="light" className="utility-matrix-summary-card">
          {dashboardData?.summary?.monthOverMonthChange >= 0 ? (
            <ArrowUp className="utility-matrix-summary-icon text-red-600" />
          ) : (
            <ArrowDown className="utility-matrix-summary-icon text-green-600" />
          )}
          <span
            className={`${textStyle(h3, 'utility-matrix-summary-value font-figtree font-bold')} ${
              dashboardData?.summary?.monthOverMonthChange >= 0 ? 'text-red-600' : 'text-green-600'
            }`}
          >
            {formatPercentage(dashboardData?.summary?.monthOverMonthChange || 0)}
          </span>
          <span className={textStyle(p, 'utility-matrix-summary-label')}>Month-over-Month Change</span>
        </Card>
      </div>

      {/* Main Content Area */}
      <div className="utility-matrix-main-content">
        {/* Chart Area */}
        <Card variant="light" className="utility-matrix-chart-card">
          <h2 className={textStyle(h3, 'utility-matrix-section-title')}>Cost by Utility Type</h2>
          <div className="utility-matrix-chart-container">
            {chartData ? (
              <Bar data={chartData} options={chartOptions} />
            ) : (
              <div className="utility-matrix-chart-placeholder">
                <p className={textStyle(p, 'text-gray-500')}>No data available for chart</p>
              </div>
            )}
          </div>
        </Card>

        {/* Insights Panel */}
        <Card variant="light" className="utility-matrix-insights-card">
          <div className="utility-matrix-insights-header">
            <AlertTriangle className="utility-matrix-insights-icon text-amber-600" />
            <h2 className={textStyle(h3, 'utility-matrix-section-title')}>Insights & Alerts</h2>
          </div>
          <div className="utility-matrix-insights-list">
            {dashboardData?.insights && dashboardData.insights.length > 0 ? (
              dashboardData.insights.map((insight, idx) => (
                <div key={idx} className="utility-matrix-insight-item">
                  <div className="utility-matrix-insight-severity">
                    <span
                      className={`utility-matrix-severity-badge ${
                        insight.severity === 'high' ? 'severity-high' : 'severity-medium'
                      }`}
                    >
                      {insight.severity === 'high' ? 'High' : 'Medium'}
                    </span>
                  </div>
                  <div className="utility-matrix-insight-content">
                    <p className={textStyle(p, 'font-semibold text-gray-900')}>{insight.message}</p>
                    <p className={textStyle(p, 'text-sm text-gray-600')}>
                      {insight.utility_type} • {insight.site_name}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className={textStyle(p, 'text-gray-500')}>No insights or alerts at this time</p>
            )}
          </div>
        </Card>
      </div>

      {/* Utility Matrix Table */}
      <Card variant="light" className="utility-matrix-table-card">
        <h2 className={textStyle(h3, 'utility-matrix-section-title mb-4')}>Utility Matrix</h2>
        <div className="utility-matrix-table-container">
          <table className="utility-matrix-table">
            <thead>
              <tr>
                <th>Utility Type</th>
                <th>Usage</th>
                <th>Cost</th>
                <th>Cost per Guest Hour</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData?.utilityMatrix && dashboardData.utilityMatrix.length > 0 ? (
                dashboardData.utilityMatrix.map((utility, idx) => (
                  <tr key={idx}>
                    <td>
                      <span className="utility-matrix-utility-type">{utility.utility_type}</span>
                    </td>
                    <td>{utility.total_usage.toLocaleString('en-IN')}</td>
                    <td>{formatCurrency(utility.total_cost)}</td>
                    <td>
                      <span
                        className={`utility-matrix-badge ${getCostPerGuestHourBadgeColor(
                          utility.cost_per_guest_hour
                        )}`}
                      >
                        {formatCurrency(utility.cost_per_guest_hour)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="utility-matrix-table-empty">
                    <p className={textStyle(p, 'text-gray-500')}>No utility data available</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default UtilityMatrixDashboard;
